import { ReloadOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import { useCallback, useEffect, useState } from "react";
import styles from "./EhrSync.module.scss";
import { SYNC_HINT, SYNC_RUNNING, SYNC_TOOLTIP, cooldownLabel, lastSyncedLabel } from "./copy";
import type { UseEhrSync } from "./useEhrSync";

export const SYNC_HINT_SEEN_KEY = "nudge.ehrSyncHintSeen";
export const SYNC_HINT_AUTO_DISMISS_MS = 6_000;

const hintAlreadySeen = (): boolean => {
  try {
    return !!localStorage.getItem(SYNC_HINT_SEEN_KEY);
  } catch {
    // Storage disabled: skip the hint rather than show it on every load.
    return true;
  }
};

const markHintSeen = () => {
  try {
    localStorage.setItem(SYNC_HINT_SEEN_KEY, "1");
  } catch {
    // Reappears next session; harmless.
  }
};

/**
 * The sidebar's sync control: an icon button in the search row, sized and bordered
 * like the sort button beside it. State lives in the tooltip and in EhrSyncStatus,
 * never in a caption beside the button, so the control costs the list no height.
 *
 * Eligibility is the parent's call (`can_sync_ehr_patients` from bootstrap plus the
 * hook's `hidden`); this component assumes it is allowed to exist.
 */
const EhrSyncButton = ({ sync }: { sync: UseEhrSync }) => {
  const { phase, lastSyncedAt, cooldownRemainingS, startSync } = sync;
  const isRunning = phase === "running";
  const isCoolingDown = phase === "cooldown";

  // One-time discoverability nudge for an icon-only control.
  const [hintOpen, setHintOpen] = useState(() => !hintAlreadySeen());
  const dismissHint = useCallback(() => {
    setHintOpen((open) => {
      if (open) markHintSeen();
      return false;
    });
  }, []);
  useEffect(() => {
    if (!hintOpen) return;
    const timer = setTimeout(dismissHint, SYNC_HINT_AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [hintOpen, dismissHint]);

  const lastSynced = lastSyncedLabel(lastSyncedAt);
  const title = (() => {
    if (hintOpen) return SYNC_HINT;
    if (isRunning) return SYNC_RUNNING;
    if (isCoolingDown) {
      return (
        <>
          {lastSynced ?? "Synced just now"}
          <br />
          Next sync in {cooldownLabel(cooldownRemainingS)}
        </>
      );
    }
    return (
      <>
        {SYNC_TOOLTIP}
        {lastSynced && (
          <>
            <br />
            <span style={{ opacity: 0.75 }}>{lastSynced}</span>
          </>
        )}
      </>
    );
  })();

  return (
    <Tooltip title={title} placement="top" open={hintOpen ? true : undefined} overlayStyle={{ maxWidth: 300 }}>
      <Button
        icon={<ReloadOutlined spin={isRunning} />}
        aria-label="Sync patients from your EHR"
        className={isRunning ? styles.buttonBusy : undefined}
        disabled={isRunning || isCoolingDown}
        onMouseEnter={dismissHint}
        onClick={() => {
          dismissHint();
          startSync();
        }}
      />
    </Tooltip>
  );
};

export default EhrSyncButton;
